import * as R from 'ramda'
import * as Downloadable from '../../../Data/Downloadable'
import * as Result from '../../../Data/Result'
import {
    PositionRow,
    PositionRowInput,
    PositionRowValidation,
    PositionsTab
} from './PositionsState'
import {PositionsSubAction} from './PositionActions'
import {OrderDetailsFeedback} from '../OrderDetailsFeedback'
import {update} from '../../../Data/ObjectHelper'
import * as ProductResource from '../../../Request/ProductResource'
import {
    PositionWithProduct,
    positionWithProductDecoder
} from '../../../Entities/PositionWithProduct'
import {
    mandatoryStringValidator,
    minimumValidator,
    stringToFixedPointNumberValidator,
    stringToIntValidator,
    validateInputField
} from '../../../Data/FormValidation'
import {Position} from '../../../Entities/Position'
import * as PositionResource from '../../../Request/PositionResource'
import {decodeHttpResponse} from '../../../Data/Http'
import {array} from 'json-bouncer'
import {filterMap, findMap} from '../../../Data/ArrayHelper'
import {PositionPatch} from '../../../Entities/PositionPatch'
import {ProductFinderFeedback} from '../../../Components/ProductFinder/ProductFinderFeedback'
import {
    initProductFinder,
    productFinderReducer
} from '../../../Components/ProductFinder/ProductFinderReducer'
import {Session} from '../../../Data/Session'
import {formatPrice} from '../../../Data/NumberHelper'
import {FeedbackLoop} from '../../../Data/FeedbackLoop'
import {ProductWithPriceDefinitions} from '../../../Entities/ProductWithPriceDefinitions'
import {calculatePrice} from '../../../Entities/PriceDefinition'

export function initPositions(
    positionsWithProducts: PositionWithProduct[],
): PositionsTab {
    return {
        positionRows: R.map(
            initPositionRow,
            positionsWithProducts),
        editable: {type: 'Locked'},
        submissionAttempted: false,
        productFinder: null,
    }
}

function initPositionRow(
    positionWithProduct: PositionWithProduct,
): PositionRow {
    return {
        id: positionWithProduct.position.id,
        mode: {type: 'Editing', positionWithProduct},
        printPdfDownload: {type: 'Default'},
        input: initInput(positionWithProduct.position),
        validation: initValidation(),
        toBeDeleted: false,
    }
}

function initInput(
    {name, amount, weight, totalNetPrice}: Position,
): PositionRowInput {
    return {
        name,
        amount: amount.toString(),
        weight: weight.toString(),
        price: totalNetPrice !== null ? formatPrice(totalNetPrice) : '',
    }
}

function initInputFromProductWithPriceDefinitions(
    {product, priceDefinitions}: ProductWithPriceDefinitions,
): PositionRowInput {
    const amount = 1
    return {
        name: product.name,
        amount: amount.toString(),
        weight: product.weight.toString(),
        price:
            formatPrice(
                Result.withDefault(0, calculatePrice(priceDefinitions, amount)),
            )
    }
}

function initValidation(): PositionRowValidation {
    return {
        name: null,
        amount: null,
        weight: null,
        price: null,
    }
}

type PositionsLoop =
    FeedbackLoop<PositionsTab, OrderDetailsFeedback>

export function positionsReducer(
    session: Session,
    customerId: number,
    orderId: number,
    action: PositionsSubAction,
    state: PositionsTab
): PositionsLoop {
    switch (action.type) {
        case 'EditPositions': {
            return {
                state: update({
                    editable: {
                        type: 'Pending',
                        value: null,
                    }
                }, state),
                feedback: [],
            }
        }
        case 'PositionAmountChanged':
            return {
                state: updatePositionRow(
                    positionRow => update({
                        input: update({
                            amount: action.value
                        }, positionRow.input),
                        validation: update({
                            amount:
                                validateInputField(
                                    validators.amount,
                                    state.submissionAttempted,
                                    action.value),
                        }, positionRow.validation),
                    }, positionRow),
                    action.id,
                    state),
                feedback: [],
            }
        case 'PositionNameChanged':
            return {
                state: updatePositionRow(
                    positionRow => update({
                        input: update({
                            name: action.value,
                        }, positionRow.input),
                        validation: update({
                            name:
                                validateInputField(
                                    validators.name,
                                    state.submissionAttempted,
                                    action.value),
                        }, positionRow.validation),
                    }, positionRow),
                    action.id,
                    state),
                feedback: [],
            }
        case 'PositionWeightChanged':
            return {
                state: updatePositionRow(
                    positionRow => update({
                        input: update({
                            weight: action.value,
                        }, positionRow.input),
                        validation: update({
                            weight:
                                validateInputField(
                                    validators.weight,
                                    state.submissionAttempted,
                                    action.value),
                        }, positionRow.validation),
                    }, positionRow),
                    action.id,
                    state),
                feedback: [],
            }
        case 'PositionPriceChanged':
            return {
                state: updatePositionRow(
                    positionRow => update({
                        input: update({
                            price: action.value,
                        }, positionRow.input),
                        validation: update({
                            price:
                                validateInputField(
                                    validators.price,
                                    state.submissionAttempted,
                                    action.value),
                        }, positionRow.validation),
                    }, positionRow),
                    action.id,
                    state),
                feedback: [],
            }
        case 'AddPosition': {
            const productFinderLoop = initProductFinder(session, customerId)
            return reduceProductFinderFeedback(
                productFinderLoop.feedback,
                update({productFinder: productFinderLoop.state}, state),
            )
        }
        case 'SavePositions':
            const validationOutcome =
                validate(state.positionRows)
            const newState = update({
                positionRows: validationOutcome.positionRows,
            }, state)
            const deletions =
                filterMap(
                    positionRow =>
                        positionRow.toBeDeleted &&
                        positionRow.mode.type === 'Editing'
                            ? positionRow.mode.positionWithProduct.position.id
                            : null,
                    newState.positionRows,
                )
            return validationOutcome.data !== null ?
                {
                    state: update({
                        editable: {type: 'Loading', value: null},
                    }, newState),
                    feedback: [{
                        type: 'SendRequest',
                        identifier: {
                            type: 'PositionBatch'
                        },
                        httpRequest: PositionResource.batch(
                            orderId,
                            validationOutcome.data,
                            deletions,
                        ),
                    }],
                } :
                {
                    state: newState,
                    feedback: [],
                }
        case 'PositionBatchResponse':
            const result =
                decodeHttpResponse(
                    array(positionWithProductDecoder()),
                    action.result)
            return (() => {
                switch (result.type) {
                    case 'Ok':
                        return {
                            state: update({
                                positionRows:
                                    R.map(
                                        initPositionRow,
                                        result.value),
                                editable: {
                                    type: 'Locked',
                                },
                            }, state),
                            feedback: [],
                        }
                    case 'Err':
                        return {
                            state: update({
                                editable: {
                                    type: 'Failure',
                                    value: null,
                                    error: result.error,
                                },
                            }, state),
                            feedback: [],
                        }
                }
            })()
        case 'PositionToBeDeletedToggle': {
            return {
                state: update(
                    {
                        positionRows:
                            filterMap(
                                row =>
                                    row.id === action.id ?
                                        (() => {
                                            switch (row.mode.type) {
                                                case 'Editing':
                                                    return update(
                                                        {toBeDeleted: action.toBeDeleted},
                                                        row,
                                                    )
                                                case 'Creating':
                                                    return action.toBeDeleted
                                                        ? null
                                                        : row
                                            }
                                        })() :
                                        row,
                                state.positionRows,
                            ),
                    },
                    state,
                ),
                feedback: [],
            }
        }
        case 'DiscardPositions':
            return {
                state: update({
                    editable: {type: 'Locked'},
                    submissionAttempted: false,
                    positionRows:
                        filterMap(
                            positionRow =>
                                positionRow.mode.type === 'Editing' ?
                                    update({
                                        input:
                                            initInput(
                                                positionRow
                                                    .mode
                                                    .positionWithProduct
                                                    .position,
                                            ),
                                        validation: initValidation(),
                                        toBeDeleted: false,
                                    }, positionRow) :
                                    null,
                            state.positionRows),
                }, state),
                feedback: [],
            }
        case 'DownloadPrintPdf': {
            const productId =
                findMap<PositionRow, number>(
                    positionRow =>
                        positionRow.id === action.id ?
                            (() => {
                                switch (positionRow.mode.type) {
                                    case 'Editing':
                                        return positionRow
                                            .mode.positionWithProduct.product.productId
                                    case 'Creating':
                                        return positionRow.mode.product.productId
                                }
                            })() :
                            null,
                    state.positionRows)
            if (productId === null) {
                return {state, feedback: []}
            }
            return {
                state: updatePositionRow(
                    positionRow =>
                        update({
                            printPdfDownload: {type: 'Loading'},
                        }, positionRow)
                    , action.id,
                    state),
                feedback: [{
                    type: 'SendDownloadRequest',
                    identifier: {
                        type: 'ProductPrintPdfDownload',
                        id: action.id,
                    },
                    httpRequest: ProductResource.downloadPrintPdf(productId),
                    filename: `print-pdf-${productId}.pdf`,
                }]
            }
        }
        case 'ProductPrintPdfDownloadResponse': {
            return {
                state: updatePositionRow(
                    positionRow =>
                        update({
                            printPdfDownload:
                                Downloadable.fromResult(action.result),
                        }, positionRow),
                    action.id,
                    state),
                feedback: [],
            }
        }
        case 'ProductFinderModuleAction': {
            if (state.productFinder !== null) {
                const productFinderLoop =
                    productFinderReducer(
                        session,
                        customerId,
                        state.productFinder,
                        action.subAction
                    )
                return reduceProductFinderFeedback(
                    productFinderLoop.feedback,
                    update({productFinder: productFinderLoop.state}, state),
                )
            } else {
                return {state, feedback: []}
            }
        }
    }
}

function reduceProductFinderFeedback(
    productFinderFeedback: ProductFinderFeedback[],
    state: PositionsTab,
): PositionsLoop {
    return R.reduce<ProductFinderFeedback, PositionsLoop>(
        (previous, feedback) => {
            switch (feedback.type) {
                case 'SendRequest':
                    return {
                        state: previous.state,
                        feedback:
                            R.append<OrderDetailsFeedback>(
                                {
                                    type: 'SendRequest',
                                    identifier: {
                                        type: 'ProductFinderModuleRequest',
                                        identifier: feedback.identifier,
                                    },
                                    httpRequest: feedback.httpRequest,
                                },
                                previous.feedback,
                            )
                    }
                case 'ProductSelected':
                    return {
                        state: update(
                            {
                                positionRows:
                                    R.append<PositionRow>(
                                        {
                                            id:
                                                (R.reduce<number, number>(
                                                    R.max,
                                                    0,
                                                    R.map(
                                                        row => row.id,
                                                        previous.state.positionRows,
                                                    ),
                                                ) + 1),
                                            mode: {
                                                type: 'Creating',
                                                product: feedback.productWithPriceDefinitions.product,
                                            },
                                            input:
                                                initInputFromProductWithPriceDefinitions(
                                                    feedback.productWithPriceDefinitions,
                                                ),
                                            validation: initValidation(),
                                            printPdfDownload: {type: 'Default'},
                                            toBeDeleted: false,
                                        },
                                        previous.state.positionRows,
                                    ),
                                productFinder: null,
                            },
                            previous.state,
                        ),
                        feedback: previous.feedback,
                    }
                case 'Canceled':
                    return {
                        state: update({productFinder: null}, previous.state),
                        feedback: previous.feedback,
                    }
            }
        },
        {state, feedback: []},
        productFinderFeedback,
    )
}

function updatePositionRow(
    fn: (positionRow: PositionRow) => PositionRow,
    id: number,
    state: PositionsTab
): PositionsTab {
    return update({
        positionRows:
            R.map(
                positionRow =>
                    positionRow.id === id ?
                        fn(positionRow) :
                        positionRow,
                state.positionRows),
    }, state)
}

const validators = {
    name: mandatoryStringValidator(),
    amount:
        (value: string) =>
            Result.andThen(
                minimumValidator(1),
                Result.andThen(
                    stringToIntValidator(),
                    mandatoryStringValidator()(value))),
    weight:
        (value: string) =>
            Result.andThen(
                minimumValidator(1),
                Result.andThen(
                    stringToIntValidator(),
                    mandatoryStringValidator()(value))),
    price:
        (value: string) =>
            Result.andThen(
                minimumValidator(0.0),
                Result.andThen(
                    stringToFixedPointNumberValidator(2),
                    mandatoryStringValidator()(value))),
}

interface ValidationOutcome {
    readonly positionRows: PositionRow[]
    readonly data: PositionPatch[] | null
}

function validate(positionRows: PositionRow[]): ValidationOutcome {
    return R.reduce(
        (previousOutcome: ValidationOutcome, positionRow: PositionRow) => {
            if (positionRow.toBeDeleted) {
                return {
                    positionRows:
                        R.append(positionRow, previousOutcome.positionRows),
                    data: previousOutcome.data,
                }
            } else {
                const
                    {input, mode} = positionRow,
                    validationResult = {
                        name: validators.name(input.name),
                        amount: validators.amount(input.amount),
                        weight: validators.weight(input.weight),
                        price: validators.price(input.price),
                    },
                    newPositionRow = update({
                        validation: {
                            name: Result.errorToNullable(validationResult.name),
                            amount: Result.errorToNullable(validationResult.amount),
                            weight: Result.errorToNullable(validationResult.weight),
                            price: Result.errorToNullable(validationResult.price),
                        },
                    }, positionRow),
                    productId =
                        (() => {
                            switch (mode.type) {
                                case 'Editing':
                                    return mode.positionWithProduct.product.productId
                                case 'Creating':
                                    return mode.product.productId
                            }
                        })(),
                    newData: PositionPatch | null =
                        validationResult.name.type === 'Ok' &&
                        validationResult.amount.type === 'Ok' &&
                        validationResult.weight.type === 'Ok' &&
                        validationResult.price.type === 'Ok' ?
                            {
                                id:
                                    mode.type === 'Editing'
                                        ? mode
                                            .positionWithProduct.position.id
                                        : null,
                                name: validationResult.name.value,
                                amount: validationResult.amount.value,
                                weight: validationResult.weight.value,
                                price: validationResult.price.value,
                                productId,
                            } :
                            null
                return {
                    positionRows:
                        R.append(newPositionRow, previousOutcome.positionRows),
                    data:
                        previousOutcome.data !== null && newData !== null
                            ? R.append(newData, previousOutcome.data)
                            : null
                }
            }
        },
        {
            positionRows: [],
            data: [],
        },
        positionRows)
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/Pages/OrderDetails/Positions/PositionsReducer.ts